import useLinkFilesToEvent from '@/api/mutations/scheduledEvent/useLinkFilesToEvent';
import useGetProjectFolderEntries from '@/api/queries/project/useGetProjectFolderEntries';
import scheduledEventKeys from '@/api/queries/scheduleEvents/keyFactory';
import LinkFilesModal from '@/ui/components/linkFilesModal/LinkFilesModal';
import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';

export interface IProps {
  disabledFiles?: {
    uploadIds: string[];
    folderEntryIds: string[];
  };
  projectNumericalId: number;
  targetEventId: string | number;
  onLinkFilesSuccess?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const LinkProjectFiles: FC<IProps> = (props) => {
  const queryClient = useQueryClient();
  const {
    projectNumericalId,
    targetEventId,
    onLinkFilesSuccess: onLinkUploadsSuccess,
    isOpen,
    onClose,
    disabledFiles = { uploadIds: [], folderEntryIds: [] },
  } = props;

  const {
    projectFolderEntries,
    isLoading,
    hasNextPage,
    fetchNextPage,
    totalCount,
  } = useGetProjectFolderEntries(projectNumericalId.toString(), {
    input: {
      entryTypes: ['IMAGE', 'PDF', 'VIDEO', 'DOCUMENT'],
    },
  });

  const { linkFilesToEvent, isLinking } = useLinkFilesToEvent({
    eventId: targetEventId,
  });

  const refetchScheduledEvent = () => {
    void queryClient.invalidateQueries({
      queryKey: scheduledEventKeys.scheduledEvent(targetEventId),
    });
  };

  return (
    <LinkFilesModal
      key={isOpen ? 'open' : 'closed'}
      files={projectFolderEntries || []}
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      isProcessing={isLinking}
      disabledFiles={disabledFiles}
      count={projectFolderEntries?.length || 0}
      hasNextPage={hasNextPage || false}
      onLoadMore={fetchNextPage}
      totalCount={totalCount}
      onSubmit={(selectedFiles) => {
        const selectedUploads = selectedFiles.filter(
          (x) => x.__typename === 'UploadFolderEntry'
        );

        const selectedDocumentInstances = selectedFiles.filter(
          (x) => x.__typename === 'DocumentInstanceFolderEntry'
        );

        if (
          selectedFiles.length >
          selectedUploads.length + selectedDocumentInstances.length
        ) {
          throw new Error(
            `Attempting to link unsupported folder entry type to Scheduled event`
          );
        }

        void linkFilesToEvent({
          uploadIds: selectedUploads.map((x) => Number(x.upload.privateId)),
          folderEntryIds: selectedDocumentInstances.map((x) => x.id),
          onSuccess: () => {
            onLinkUploadsSuccess?.();
            refetchScheduledEvent();
          },
          onError: () => {},
        });
      }}
    />
  );
};

export default LinkProjectFiles;
